/* Column names for the card view (2026-10-26; reports added 2026-10-27; any width 2026-10-27).
 *
 * A table shows as cards on a phone, and at any width when it doesn't fit (helper/fitCards.js,
 * CSS in assets/css/mobile.css). Each row is a card, and the card needs every cell to carry its
 * column name. This copies each column's header text onto that column's cells as data-label, for
 * the tables that opt in with className="pc-card-table" (form line items, Weekly Sale's daily
 * breakdown, Shop / Account Payment entries, dashboards), the report grids ("pc-swipe-table") and
 * PhoneClinicGrid lists ("pc-grid-table"). On a phone PhoneClinicGrid labels its own cells
 * (it adds "pc-grid-cards" there), so those are left to it.
 *
 * Tables are laid out as a real grid, so colspan AND rowspan are honoured both in the header (a
 * grouped header such as "Payment By Account | Payment By Shop" over the column names) and in the
 * body (Warehouse Balance's delivery amount spans its week's rows, which shifts every later cell
 * of those rows one column right). When two columns share a name ("Amount" under each group), the
 * group goes in front: "Payment By Shop · Amount".
 *
 * It only adds a data-* attribute, which has no effect on layout unless the table is in card view.
 * React does not manage these attributes, so it never removes or fights them. helper/fitCards.js
 * runs it whenever tables change, before it decides card or table.
 */
const SELECTOR = 'table.pc-card-table, table.pc-swipe-table, table.pc-grid-table:not(.pc-grid-cards)';

const text = (cell) => (cell ? (cell.textContent || '').trim() : '');

/* Lay `rows` out as a grid: position (first column, span) of every cell, honouring rowspan. */
function layout(rows) {
    const taken = [];
    const pos = new Map();
    rows.forEach((tr, r) => {
        taken[r] = taken[r] || [];
        let c = 0;
        for (const cell of tr.cells) {
            while (taken[r][c]) c++;
            const rs = Math.max(1, cell.rowSpan || 1);
            const cs = Math.max(1, cell.colSpan || 1);
            for (let i = 0; i < rs; i++) {
                taken[r + i] = taken[r + i] || [];
                for (let j = 0; j < cs; j++) taken[r + i][c + j] = cell;
            }
            pos.set(cell, { col: c, span: cs });
            c += cs;
        }
    });
    return { taken, pos };
}

/* One label per column, from the header: the bottom-most header cell over the column, with its
 * group in front when the name alone is ambiguous. */
function headerLabels(table) {
    const rows = table.tHead ? [...table.tHead.rows] : [];
    if (rows.length === 0) return [];
    const { taken } = layout(rows);
    const last = rows.length - 1;
    const width = Math.max(0, ...taken.map((r) => (r ? r.length : 0)));
    const leaf = [];
    const group = [];
    for (let c = 0; c < width; c++) {
        let cell = null;
        for (let r = last; r >= 0 && !cell; r--) cell = taken[r] && taken[r][c];
        leaf[c] = text(cell);
        const top = taken[0] && taken[0][c];
        group[c] = top && top !== cell ? text(top) : '';
    }
    const count = {};
    leaf.forEach((l) => { count[l] = (count[l] || 0) + 1; });
    return leaf.map((l, c) => (l && count[l] > 1 && group[c] && group[c] !== l ? `${group[c]} · ${l}` : l));
}

function labelRows(rows, labels, isFooter) {
    const { pos } = layout(rows);
    for (const tr of rows) {
        for (const cell of tr.cells) {
            const p = pos.get(cell) || { col: 0, span: 1 };
            // A cell spanning several columns is a section bar, message ("No records") or caption,
            // not a value; so is the first cell of a totals row ("Net Purchase", "Total").
            const label = p.span > 1 || (isFooter && p.col === 0) ? '' : (labels[p.col] || '');
            if (cell.getAttribute('data-label') !== label) cell.setAttribute('data-label', label);
        }
    }
}

/* Free-text columns wrap (user, 2026-10-28: "if the description is long, don't force it on a single
 * line"). Every body cell under a header named Description / Detail(s) / Remarks / Notes gets
 * data-pc-wrap; index.css lets those cells wrap at their spaces (words stay whole) with a sensible
 * minimum width, while every other cell keeps the one-line rule. Matched on the column's own name
 * (the part after "·" for a grouped header). Only an attribute is set — the MutationObserver does
 * not watch attributes, so this never re-triggers itself. */
const WRAP_HEADER = /^(description|desc|details?|remarks?|notes?)$/i;

export function markWrapColumns(table) {
    const labels = headerLabels(table).map((l) => l.split('·').pop().trim());
    const cols = new Set(labels.map((l, i) => (WRAP_HEADER.test(l) ? i : -1)).filter((i) => i >= 0));
    const sections = [...table.tBodies, ...(table.tFoot ? [table.tFoot] : [])];
    for (const section of sections) {
        const { pos } = layout([...section.rows]);
        for (const tr of section.rows) {
            for (const cell of tr.cells) {
                const p = pos.get(cell);
                const wrap = !!p && p.span === 1 && cols.has(p.col);
                if (wrap && !cell.hasAttribute('data-pc-wrap')) cell.setAttribute('data-pc-wrap', '');
                else if (!wrap && cell.hasAttribute('data-pc-wrap')) cell.removeAttribute('data-pc-wrap');
            }
        }
    }
}

export function labelCardTables(root = document) {
    root.querySelectorAll(SELECTOR).forEach((table) => {
        const labels = headerLabels(table);
        for (const body of table.tBodies) labelRows([...body.rows], labels, false);
        if (table.tFoot) labelRows([...table.tFoot.rows], labels, true);
    });
}
